// Recommendations tab: the team-level port of the OSS "Recommendations"
// (insights) view. Ported section-for-section from the OSS client/insights.ts —
// the list of recommendation rows (signal + recommended action + stats) and the
// detail drawer (description → evidence → fix → dismiss) are the same UI; the
// class names and copy are kept verbatim so it reads identically.
//
// Team/org deltas layered on top (design §0.1):
//   - a scope bar (mountScopeBar) so a team-lead/admin can switch team/repo;
//     every fetch carries scopeQs(), so the card recomputes for that scope.
//   - the cards are TEAM/ORG-grain (the route forces userId null), so a member
//     sees their team's recommendations, never a personal list.
//   - Dismiss is team-lead/admin only: members don't get the button (the server
//     also 403s the write). Copy still works for everyone.
//
// The detail renders into the SAME shared right drawer the sessions tab uses
// (#drawer / #overlay / #drawerBody), so an occurrence row can hand off to the
// transcript via openDetail without a second drawer.
import { state, $, esc, get, post, num, dayOf, renderMd, scopeQs } from './core'
import { openDetail, closeDrawer } from './sessions'
import { mountScopeBar, hasScopeControls } from './filterbar'

// Last fetched rows, so a dismiss can re-render the list without a scope refetch.
var rows: any[] = [];
// Evidence cache per insight id (the drawer re-opens without refetching).
var occCache: Record<string, any[]> = {};

// scopeQs() emits '&'-prefixed params; a POST/GET that starts its own query
// needs the first flipped to '?'. '' stays '' (org/default scope server-side).
function scopeQ(): string {
  var s = scopeQs();
  return s ? '?' + s.slice(1) : '';
}

// Only a team-lead or admin may act on a team recommendation (design §0.1);
// members see the card + its state but not the Dismiss control. The server
// re-checks (403), so this is the affordance, not the boundary.
function canAct(): boolean {
  var role = state.me && state.me.role;
  return role === 'admin' || role === 'team-lead';
}

function isGlobal(repo: string): boolean { return !repo || repo === '*'; }
function repoLabel(repo: string): string { return isGlobal(repo) ? 'global' : repo; }

// fix.type → rendering, per the detector contract: prose for a behavioral nudge,
// a code block for the paste/run payload types (config-snippet | install | fix-prompt).
function fixBody(fix: any): string {
  if (fix.type === 'behavioral-nudge') return '<div class="ins-fix-prose">' + renderMd(fix.content) + '</div>';
  return '<pre class="md-code"><code>' + esc(fix.content) + '</code></pre>';
}

function fixAffordance(type: string): { hint: string; copy: string } {
  if (type === 'fix-prompt') return { hint: 'Paste this into your coding agent.', copy: 'Copy prompt' };
  if (type === 'config-snippet') return { hint: 'Apply this to your config.', copy: 'Copy' };
  if (type === 'install-command') return { hint: 'Run this in your terminal.', copy: 'Copy command' };
  return { hint: '', copy: 'Copy fix' };
}

function shortSession(id: string): string {
  var parts = String(id).split(':');
  return (parts[parts.length - 1] || id).slice(0, 8);
}

function sessionSpan(r: any): number {
  if (typeof r.sessionCount === 'number') return r.sessionCount;
  var seen: Record<string, number> = {};
  (r.evidence || []).forEach(function (e: any) { seen[e.sessionId] = 1; });
  return Object.keys(seen).length;
}

// ---- detail drawer ----------------------------------------------------------

function detailHtml(r: any, occ: any[] | null): string {
  var head = '<div class="drawer-head ins-detail-head"><div class="drawer-head-top">' +
    '<h2>' + esc(r.title) + '</h2>' +
    '<button class="x" type="button" id="drawerCloseBtn">close</button></div>' +
    '<div class="ins-detail-meta">' +
      '<span class="tag">' + esc(repoLabel(r.repo)) + '</span> · ' +
      num(r.count) + ' occurrences · last seen ' + esc(dayOf(r.lastSeenAt)) + '</div>' +
    '</div>';

  var desc = '<div class="ins-desc">' + renderMd(r.description) + '</div>';

  var aff = fixAffordance(r.fix && r.fix.type);
  var fix = r.fix && r.fix.content
    ? '<div class="ins-section-label">Fix</div>' +
      '<div class="ins-fix"><div class="ins-fix-head">' +
        '<span class="ins-fix-label">' + esc(r.fix.label || 'Suggested fix') + '</span>' +
        '<button type="button" class="ins-btn ins-copy">' + esc(aff.copy) + '</button></div>' +
        (aff.hint ? '<div class="ins-fix-hint">' + esc(aff.hint) + '</div>' : '') +
        fixBody(r.fix) + '</div>'
    : '';

  var occRows = (occ || []).map(function (e: any, j: number) {
    var title = e.sessionTitle ? esc(e.sessionTitle) : esc(shortSession(e.sessionId));
    // Whose session this is — evidence now spans users at team/org scope, so the
    // owner (and team, lightly) sits to the right of the note line.
    var owner = e.ownerName
      ? '<span class="ins-occ-owner">' + esc(e.ownerName) + (e.ownerTeam ? ' · ' + esc(e.ownerTeam) : '') + '</span>'
      : '';
    var noteEl = e.note ? '<div class="ins-occ-note">' + esc(e.note) + '</div>' : '<span></span>';
    var top = (e.note || owner) ? '<div class="ins-occ-top">' + noteEl + owner + '</div>' : '';
    return '<button type="button" class="ins-occ" data-j="' + j + '" title="Open transcript at this message">' +
      top +
      '<div class="ins-occ-src"><span class="ins-occ-arrow">↳</span>' +
        '<span class="ins-occ-title">' + title + '</span>' +
        (e.turnIdx != null ? '<span class="ins-occ-turn">open ↗</span>' : '') +
      '</div></button>';
  }).join('');
  var evSessions = sessionSpan(r);
  var occSection = '<div class="ins-occ-list"><div class="ins-section-label">Evidence (' +
    num(evSessions) + ' session' + (evSessions === 1 ? '' : 's') + ')</div>' +
    '<div class="ins-occ-scroll">' + (occRows || '<div class="empty">No stored occurrences.</div>') + '</div></div>';

  // Team/org delta: only a lead/admin gets the Dismiss control; a member sees the
  // card's state (an adopted/dismissed recommendation just won't appear) but can't act.
  var dismiss = canAct()
    ? '<div class="ins-detail-actions">' +
      '<button type="button" class="ins-btn ins-dismiss">Dismiss recommendation</button></div>'
    : '';

  // Order: description (why) → evidence (receipts, capped/scrollable) → fix (what to do) → dismiss.
  return head + '<div class="ins-detail-body">' + desc + occSection + fix + dismiss + '</div>';
}

function copyFix(r: any, btn: HTMLElement): void {
  navigator.clipboard.writeText(r.fix.content).then(function () {
    var orig = btn.textContent;
    btn.textContent = 'Copied ✓';
    setTimeout(function () { btn.textContent = orig; }, 1500);
    // fix-issued is a lifecycle write (lead/admin only server-side); a member's
    // copy still works, the state transition just no-ops. Scope carried so the
    // server resolves the id to the same scoped card the viewer sees.
    post('/api/insights/fix-issued' + scopeQ(), { id: r.id }).then(function (res: any) {
      if (!res || !res.ok) return;
      r.state = 'fix_issued';
      paint();
    }).catch(function () { /* member copy: write gated, ignore */ });
  }, function () {
    var orig = btn.textContent;
    btn.textContent = 'Copy failed';
    setTimeout(function () { btn.textContent = orig; }, 1500);
  });
}

function openInsight(r: any): void {
  var render = function (occ: any[] | null) {
    var body = $('#drawerBody');
    if (!body) return;
    body.innerHTML = detailHtml(r, occ);
    body.setAttribute('data-insight-id', r.id);
    $('#drawer').classList.add('on');
    $('#overlay').classList.add('on');
    wireDetail(r, occ);
  };
  if (occCache[r.id]) { render(occCache[r.id]); return; }
  render(null);
  get('/api/insight/evidence' + scopeQ() + (scopeQ() ? '&' : '?') + 'id=' + encodeURIComponent(r.id)).then(function (occ: any) {
    occCache[r.id] = Array.isArray(occ) ? occ : [];
    var body = $('#drawerBody');
    if (body && $('#drawer').classList.contains('on') && body.getAttribute('data-insight-id') === r.id) {
      render(occCache[r.id]);
    }
  });
}

function wireDetail(r: any, occ: any[] | null): void {
  var close = $('#drawerCloseBtn');
  if (close) close.onclick = closeDrawer;

  var copy = $('#drawerBody .ins-copy');
  if (copy) copy.onclick = function () { copyFix(r, copy); };

  var dismiss = $('#drawerBody .ins-dismiss');
  if (dismiss) dismiss.onclick = function () {
    if (!window.confirm('Dismiss "' + r.title + '"? It will not resurface, even if the pattern recurs.')) return;
    post('/api/insights/dismiss' + scopeQ(), { id: r.id }).then(function () { closeDrawer(); renderInsights(); });
  };

  // An occurrence opens the session in the shared drawer. openDetail replaces the
  // drawer body (same as the Skills evidence rows) — no "back to insight" step, as
  // the enterprise drawer has no stacking for insights.
  Array.prototype.forEach.call(document.querySelectorAll('#drawerBody .ins-occ'), function (b: HTMLElement) {
    b.onclick = function () {
      var e = (occ || [])[parseInt(b.getAttribute('data-j') || '0', 10)];
      if (!e) return;
      // Carry a "back to Recommendations" step into the session drawer (OSS parity):
      // clicking it re-renders this insight into the shared drawer.
      openDetail(e.sessionId, {
        toolTarget: e.turnIdx == null ? undefined : e.turnIdx,
        back: function () { openInsight(r); },
        backLabel: 'Recommendations',
      });
    };
  });
}

// ---- list -------------------------------------------------------------------

function row(r: any, i: number): string {
  var span = sessionSpan(r);
  var rec = num(r.count) + ' occ' + (span >= 1 ? ' · ' + num(span) + (span === 1 ? ' session' : ' sessions') : '');
  var repoTag = isGlobal(r.repo) ? '' : '<span class="tag ins-row-repo" title="' + esc(r.repo) + '">' + esc(r.repo) + '</span>';
  var recLine = r.recommendation
    ? '<div class="ins-row-rec-line"><span class="ins-rec-arrow">↳</span>' + esc(r.recommendation) + '</div>'
    : '';
  var meta = '<div class="ins-row-meta">' + esc(rec) + ' · last seen ' + esc(dayOf(r.lastSeenAt)) + '</div>';
  return '<div class="ins-row" data-i="' + i + '" data-id="' + esc(r.id) + '">' +
    '<div class="ins-row-main">' +
      '<div class="ins-row-title-wrap"><span class="ins-row-title" title="' + esc(r.title) + '">' + esc(r.title) + '</span>' + repoTag + '</div>' +
      recLine + meta +
    '</div>' +
    '<button type="button" class="hrow-to ins-row-view" data-i="' + i + '">View <i>→</i></button>' +
    '</div>';
}

function selectRow(block: HTMLElement | null): void {
  Array.prototype.forEach.call(document.querySelectorAll('.ins-row'), function (b: HTMLElement) { b.classList.remove('on'); });
  if (block) block.classList.add('on');
}

function paint(): void {
  var el = $('#insights');
  if (!el) return;
  var scopeBar = hasScopeControls({ noUser: true }) ? '<div class="filter-bar ins-scope" id="insights-fb"></div>' : '';
  var head = '<section class="panel"><div class="panel-head"><h2>Recommendations</h2></div>' + scopeBar;

  if (!rows.length) {
    el.innerHTML = head +
      '<div class="empty">No recommendations yet. They surface here as your team\'s session transcripts are analyzed.</div></section>';
    mountInsightsScope();
    return;
  }

  el.innerHTML = head +
    '<div class="hl-head ins-intro">Recommendations from analyzing your team\'s session transcripts</div>' +
    '<div class="ins-list">' + rows.map(function (r, i) { return row(r, i); }).join('') + '</div></section>';
  mountInsightsScope();

  Array.prototype.forEach.call(el.querySelectorAll('.ins-row'), function (block: HTMLElement) {
    var open = function () {
      var r = rows[parseInt(block.getAttribute('data-i') || '0', 10)];
      if (r) { selectRow(block); openInsight(r); }
    };
    block.onclick = function () {
      if (window.getSelection && String(window.getSelection()).length > 0) return; // user was selecting text
      open();
    };
    var view = block.querySelector('.ins-row-view') as HTMLElement | null;
    if (view) view.onclick = function (e) { e.stopPropagation(); open(); };
  });
}

function mountInsightsScope(): void {
  var fb = document.getElementById('insights-fb');
  if (fb) mountScopeBar(fb as HTMLElement, renderInsights, { noUser: true });
}

export function renderInsights(): void {
  get('/api/insights' + scopeQ()).then(function (d: any) {
    if (state.view !== 'insights') return; // navigated away meanwhile
    rows = Array.isArray(d) ? d : [];
    occCache = {}; // scope/refresh changed — drop cached evidence
    paint();
  });
}
